import { fonts } from "@/constants/fonts";
import { useThemeColor } from "@/hooks/useThemeColor";
import React, { useRef, useState } from "react";
import {
  StyleSheet,
  TextInput as RNTextInput,
  View,
  type ViewStyle,
} from "react-native";
import ThemedText from "../core/ThemedText";

interface OTPInputProps {
  length?: number;
  value?: string;
  onChangeText?: (value: string) => void;
  onComplete?: (value: string) => void;
  error?: string;
  label?: string;
  disabled?: boolean;
  containerStyle?: ViewStyle;
  secure?: boolean;
}

export const OTPInput: React.FC<OTPInputProps> = ({
  length = 6,
  value = "",
  onChangeText,
  onComplete,
  error,
  label,
  disabled = false,
  containerStyle,
  secure = false,
}) => {
  const backgroundColor = useThemeColor({}, "backgroundTertiary");
  const textColor = useThemeColor({}, "text");
  const borderColor = useThemeColor({}, "border");
  const primaryColor = useThemeColor({}, "primary");
  const errorColor = useThemeColor({}, "error");
  const disabledTextColor = useThemeColor({}, "textDisabled");
  const disabledBackgroundColor = useThemeColor({}, "backgroundDisabled");

  const [focused, setFocused] = useState(false);
  const inputRefs = useRef<Array<RNTextInput | null>>([]);

  const digits = value.split("").slice(0, length);
  while (digits.length < length) {
    digits.push("");
  }

  const handleChangeText = (text: string, index: number) => {
    // Only allow numeric input
    const numeric = text.replace(/[^0-9]/g, "");

    if (numeric.length === 0) {
      // Handle deletion
      const newValue = digits.map((d, i) => (i === index ? "" : d)).join("");
      onChangeText?.(newValue);

      // Move to previous input on delete
      if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
      return;
    }

    if (numeric.length === 1) {
      // Single digit input
      const newDigits = [...digits];
      newDigits[index] = numeric;
      const newValue = newDigits.join("");
      onChangeText?.(newValue);

      // Auto-focus next input
      if (index < length - 1 && numeric) {
        inputRefs.current[index + 1]?.focus();
      }

      // Call onComplete if all digits are filled
      if (index === length - 1 && newDigits.every((d) => d !== "")) {
        onComplete?.(newValue);
      }
    } else if (numeric.length > 1) {
      // Handle paste or multiple digits
      const pastedDigits = numeric.split("").slice(0, length);
      const newDigits = [...digits];

      for (let i = 0; i < pastedDigits.length && index + i < length; i++) {
        newDigits[index + i] = pastedDigits[i];
      }

      const newValue = newDigits.join("");
      onChangeText?.(newValue);

      // Focus on the next empty input or the last one
      const nextEmptyIndex = newDigits.findIndex((d, i) => i > index && d === "");
      if (nextEmptyIndex !== -1) {
        inputRefs.current[nextEmptyIndex]?.focus();
      } else if (newDigits.every((d) => d !== "")) {
        inputRefs.current[length - 1]?.blur();
        onComplete?.(newValue);
      }
    }
  };

  const handleKeyPress = (e: any, index: number) => {
    if (e.nativeEvent.key === "Backspace" && !digits[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleFocus = (index: number) => {
    setFocused(true);
    // Select the text when focusing
    setTimeout(() => {
      inputRefs.current[index]?.setNativeProps({
        selection: { start: 0, end: 1 },
      });
    }, 10);
  };

  const handleBlur = () => {
    setFocused(false);
  };

  return (
    <View style={[styles.container, containerStyle]}>
      {label && (
        <ThemedText
          style={[styles.label, disabled && { color: disabledTextColor }]}
        >
          {label}
        </ThemedText>
      )}
      <View style={styles.inputsContainer}>
        {digits.map((digit, index) => (
          <View
            key={index}
            style={[
              styles.inputWrapper,
              {
                backgroundColor: disabled
                  ? disabledBackgroundColor
                  : backgroundColor,
                borderColor: error
                  ? errorColor
                  : focused && digits[index]
                  ? primaryColor
                  : borderColor,
              },
              disabled && styles.disabled,
            ]}
          >
            <RNTextInput
              ref={(ref) => { inputRefs.current[index] = ref; }}
              style={[
                styles.input,
                {
                  color: disabled ? disabledTextColor : textColor,
                },
              ]}
              value={secure && digit ? "•" : digit}
              onChangeText={(text) => handleChangeText(text, index)}
              onKeyPress={(e) => handleKeyPress(e, index)}
              onFocus={() => handleFocus(index)}
              onBlur={handleBlur}
              keyboardType="number-pad"
              maxLength={1}
              selectTextOnFocus
              editable={!disabled}
              autoComplete="off"
              autoCorrect={false}
              textContentType="oneTimeCode"
              // iOS-specific: helps with autofill from SMS
              {...(index === 0 && { textContentType: "oneTimeCode" })}
            />
          </View>
        ))}
      </View>
      {error && (
        <ThemedText style={[styles.error, { color: errorColor }]}>
          {error}
        </ThemedText>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  label: {
    marginBottom: 8,
    fontSize: 14,
    fontFamily: fonts.medium,
  },
  inputsContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 8,
  },
  inputWrapper: {
    flex: 1,
    height: 56,
    borderRadius: 12,
    borderWidth: 2,
    alignItems: "center",
    justifyContent: "center",
  },
  input: {
    fontSize: 24,
    fontFamily: fonts.bold,
    textAlign: "center",
    width: "100%",
    height: "100%",
  },
  disabled: {
    opacity: 0.5,
  },
  error: {
    marginTop: 4,
    fontSize: 12,
    fontFamily: fonts.regular,
  },
});

export default OTPInput;
